import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, StatusBadge } from "@/components/StatusBits";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

const Card = ({ title, ok, okText = "Configured", children, testId }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-3" data-testid={testId}>
    <div className="flex items-center justify-between">
      <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
      <StatusBadge tone={ok ? "success" : "muted"} value="x" testId={`${testId}-status`}>{ok ? okText : "Not configured"}</StatusBadge>
    </div>
    {children}
  </div>
);

const useAction = (refresh) => async (fn, msg) => {
  try {
    await fn();
    if (msg) toast.success(msg);
    await refresh();
  } catch (e) {
    toast.error(errMsg(e));
  }
};

export const IntegrationsTab = () => {
  const { status, refresh, isAdmin } = useApp();
  const [tw, setTw] = useState({ account_sid: "", auth_token: "" });
  const [el, setEl] = useState("");
  const [aiKey, setAiKey] = useState("");
  const [url, setUrl] = useState("");
  const act = useAction(refresh);
  useEffect(() => {
    setUrl(status?.public_url || "");
  }, [status]);
  if (!isAdmin) return <p className="text-sm text-slate-500">Only admins can change integrations.</p>;
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Card title="Public URL" ok={status?.public_url?.startsWith("https://")} testId="integration-public-url">
        <p className="text-xs text-slate-500">The address Twilio uses to reach this site for call webhooks and audio. Update it after you publish or connect a custom domain.</p>
        <div className="flex gap-2">
          <Input data-testid="public-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://your-site.com" />
          <Button data-testid="public-url-save-btn" onClick={() => act(() => api.put("/integrations/public-url", { url }), "Public URL saved")}>Save</Button>
        </div>
      </Card>
      <Card title="Twilio (voice & SMS)" ok={status?.twilio?.configured} okText={status?.twilio?.browser_ready ? "Configured · browser ready" : "Configured · app setup needed"} testId="integration-twilio">
        <p className="text-xs text-slate-500">Credentials are stored encrypted for this site only. A dedicated TwiML App and API key are created for Phone Blower — other apps on your Twilio account are not changed.</p>
        {status?.twilio?.configured && <p className="text-xs">Account: <span className="font-mono">{status.twilio.account_sid_hint}</span></p>}
        <div className="grid sm:grid-cols-2 gap-2">
          <Field label="Account SID"><Input data-testid="twilio-sid-input" value={tw.account_sid} onChange={(e) => setTw({ ...tw, account_sid: e.target.value })} placeholder="AC…" /></Field>
          <Field label="Auth token"><Input type="password" data-testid="twilio-token-input" value={tw.auth_token} onChange={(e) => setTw({ ...tw, auth_token: e.target.value })} /></Field>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="twilio-save-btn" disabled={!tw.account_sid || !tw.auth_token} onClick={() => act(() => api.put("/integrations/twilio", tw), "Twilio connected")}>Save & verify</Button>
          <Button variant="outline" data-testid="twilio-setup-app-btn" disabled={!status?.twilio?.configured} onClick={() => act(() => api.post("/integrations/twilio/setup-app"), "Browser calling app ready")}>Set up browser calling app</Button>
          {status?.twilio?.configured && <Button variant="ghost" className="text-rose-600" data-testid="twilio-clear-btn" onClick={() => act(() => api.delete("/integrations/twilio"), "Twilio removed")}>Remove</Button>}
        </div>
        {status?.twilio?.configured && <p className="text-xs text-slate-600" data-testid="numbers-tab-hint">Your numbers are in the <b>Phone numbers</b> tab, with where each one is linked.</p>}
      </Card>
      <Card title="ElevenLabs (AI voice)" ok={status?.elevenlabs?.configured} testId="integration-elevenlabs">
        <div className="flex gap-2">
          <Input type="password" data-testid="elevenlabs-key-input" value={el} onChange={(e) => setEl(e.target.value)} placeholder="ElevenLabs API key" />
          <Button data-testid="elevenlabs-save-btn" disabled={!el} onClick={() => act(() => api.put("/integrations/elevenlabs", { api_key: el }), "ElevenLabs connected")}>Save & verify</Button>
        </div>
        {status?.elevenlabs?.configured && <Button variant="ghost" size="sm" className="text-rose-600" data-testid="elevenlabs-clear-btn" onClick={() => act(() => api.delete("/integrations/elevenlabs"), "Removed")}>Remove</Button>}
      </Card>
      <Card title="AI provider (conversation & summaries)" ok={status?.ai?.configured} okText={status?.ai?.source === "own_key" ? "Configured · your Anthropic key" : "Configured · Emergent universal key"} testId="integration-ai">
        <p className="text-xs text-slate-500">Model: <span className="font-mono">{status?.ai?.model}</span>. Uses the Emergent universal key by default; optionally use your own Anthropic key.</p>
        <div className="flex gap-2">
          <Input type="password" data-testid="ai-key-input" value={aiKey} onChange={(e) => setAiKey(e.target.value)} placeholder="Optional: your Anthropic API key" />
          <Button data-testid="ai-save-btn" disabled={!aiKey} onClick={() => act(() => api.put("/integrations/ai", { api_key: aiKey }), "AI key saved")}>Save</Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="ai-test-btn" onClick={() => act(async () => { const r = await api.post("/integrations/ai/test"); toast.success(`AI replied: ${r.data.reply}`); })}>Test connection</Button>
          {status?.ai?.source === "own_key" && <Button variant="ghost" size="sm" className="text-rose-600" data-testid="ai-clear-btn" onClick={() => act(() => api.delete("/integrations/ai"), "Using universal key")}>Use universal key</Button>}
        </div>
      </Card>
    </div>
  );
};
